import { useState, useEffect } from "react";
import axios from "axios";
import { Eye, Trash2, CheckCircle, Clock, XCircle, Search, Download, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABELS = { pending: "Pendiente", confirmed: "Confirmada", cancelled: "Cancelada" };
const STATUS_COLORS = { pending: "bg-yellow-100 text-yellow-800", confirmed: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-800" };
const STATUS_ICONS = { pending: Clock, confirmed: CheckCircle, cancelled: XCircle };

// Helper: devuelve nombre del niño (nuevo formato flat o legacy con ninos array)
function getParticipantes(reg) {
  if (reg.nombre_nino) return reg.nombre_nino;
  const ninos = reg.ninos;
  if (Array.isArray(ninos) && ninos.length > 0) {
    return ninos.map(n => n.nombre_nino || "").filter(Boolean).join(", ");
  }
  return "—";
}

// Helper: devuelve resumen de tallas
function getTallas(reg) {
  if (reg.talla_camiseta && !Array.isArray(reg.ninos)) return reg.talla_camiseta;
  const ninos = reg.ninos;
  if (Array.isArray(ninos) && ninos.length > 0) {
    return ninos.map(n => n.talla_camiseta || "").filter(Boolean).join(", ");
  }
  return reg.talla_camiseta || "—";
}

function RegistrationModal({ reg, onClose, onStatusChange }) {
  const [status, setStatus] = useState(reg.status);
  const [saving, setSaving] = useState(false);
  // Legacy multi-child (old ninos array format)
  const legacyNinos = Array.isArray(reg.ninos) && reg.ninos.length > 1 ? reg.ninos : null;
  const isFamilia = reg.num_hermanos > 1;

  const handleStatus = async (newStatus) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/registrations/${reg._id}/status`, { status: newStatus });
      setStatus(newStatus);
      onStatusChange(reg._id, newStatus);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const sharedRows = [
    ["Tutor/a", reg.nombre_tutor], ["Teléfono", reg.telefono_contacto], ["Email", reg.email_contacto],
    ["Dirección", `${reg.direccion || "—"} ${reg.poblacion ? `(${reg.poblacion})` : ""}`.trim()],
    ["Semana 1", reg.semana_1 ? "Sí" : "No"], ["Semana 2", reg.semana_2 ? "Sí" : "No"],
    ["Madrugadores", reg.madrugadores ? "Sí" : "No"], ["Recogida ampliada", reg.recogida_ampliada ? "Sí" : "No"],
    ["Precio individual", reg.precio_total ? `${reg.precio_total}€` : "—"],
    ["Autoriza participación", reg.autoriza_participacion ? "Sí" : "No"],
    ["Autoriza imágenes", reg.autoriza_imagenes ? "Sí" : "No"],
    ["Firmante", reg.nombre_firmante], ["Fecha firma", reg.fecha_firma],
    ["Fecha inscripción", reg.created_at ? new Date(reg.created_at).toLocaleDateString("es-ES") : "—"],
  ];

  const childRows = [
    ["Fecha nacimiento", `${reg.fecha_nacimiento || "—"}${reg.edad ? ` (${reg.edad} años)` : ""}`],
    ["Talla camiseta", reg.talla_camiseta || "—"],
    ["DNI", reg.dni_nino || "—"],
    ["Alergias", reg.alergias || "—"],
    ["Intolerancias", reg.intolerancias || "—"],
    ["Medicación/Enfermedades", reg.enfermedades_medicacion || "—"],
    ["BTT Bicicleta", reg.btt_bicicleta ? "Sí" : "No"],
    ["BTT Cámaras gel", reg.btt_camaras_gel ? "Sí" : "No"],
    ["Observaciones", reg.observaciones || "—"],
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="registration-modal">
        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-['Montserrat'] font-bold text-lg text-[#0F172A]">
              {getParticipantes(reg)}
            </h2>
            {isFamilia && (
              <span className="inline-block mt-1 text-[11px] font-['Montserrat'] font-semibold bg-[#3CB44B]/10 text-[#3CB44B] px-2 py-0.5 rounded-full">
                Inscripción familiar · {reg.num_hermanos} hermanos/as
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#475569] transition-colors" data-testid="close-modal-btn">✕</button>
        </div>

        {/* Status changer */}
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center gap-3">
          <span className="font-['Montserrat'] text-sm text-[#475569]">Estado:</span>
          {Object.keys(STATUS_LABELS).map(s => (
            <button key={s} onClick={() => handleStatus(s)} disabled={saving} data-testid={`status-btn-${s}`}
              className={`px-3 py-1 rounded-full font-['Montserrat'] text-xs font-semibold transition-all ${status === s ? STATUS_COLORS[s] + " ring-2 ring-offset-1 ring-[#0072A4]" : "bg-[#F0F8FF] text-[#475569] hover:bg-[#E0F0FF]"}`}>
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Participant data — new flat format */}
          {!legacyNinos && (
            <div>
              <p className="font-['Montserrat'] font-semibold text-[#0072A4] text-sm mb-2">Datos del participante</p>
              <div className="bg-[#F0F8FF] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {childRows.map(([label, value]) => (
                      <tr key={label} className="border-b border-[#E8F4FB] last:border-0">
                        <td className="px-4 py-2 font-['Montserrat'] font-semibold text-[#475569] w-[45%]">{label}</td>
                        <td className="px-4 py-2 font-['Montserrat'] text-[#0F172A]">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Legacy multi-child format */}
          {legacyNinos && (
            <div>
              <p className="font-['Montserrat'] font-semibold text-[#0072A4] text-sm mb-2">
                Participantes ({legacyNinos.length} niños/as)
              </p>
              <div className="space-y-2">
                {legacyNinos.map((n, i) => (
                  <div key={i} className="bg-[#F0F8FF] rounded-lg px-4 py-3 flex flex-wrap gap-x-6 gap-y-1 text-sm font-['Montserrat']">
                    <span><strong>{i + 1}.</strong> {n.nombre_nino}</span>
                    <span className="text-[#475569]">Nacimiento: {n.fecha_nacimiento}</span>
                    <span className="text-[#475569]">Edad: {n.edad} años</span>
                    <span className="text-[#475569]">Talla: <strong>{n.talla_camiseta}</strong></span>
                    {n.dni_nino && <span className="text-[#475569]">DNI: {n.dni_nino}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Shared data */}
          <div>
            <p className="font-['Montserrat'] font-semibold text-[#0072A4] text-sm mb-2">Inscripción y contacto</p>
            <table className="w-full text-sm">
              <tbody>
                {sharedRows.map(([label, value]) => (
                  <tr key={label} className="border-b border-[#F1F5F9] last:border-0">
                    <td className="py-2 font-['Montserrat'] font-semibold text-[#475569] w-1/2">{label}</td>
                    <td className="py-2 font-['Montserrat'] text-[#0F172A]">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RegistrationsManager() {
  const [registrations, setRegistrations] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterWeek, setFilterWeek] = useState("all");
  const [selected, setSelected] = useState(null);

  const fetchRegs = () => axios.get(`${API}/registrations`, { withCredentials: true }).then(r => setRegistrations(r.data)).catch(() => {});
  useEffect(() => { fetchRegs(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta inscripción?")) return;
    await axios.delete(`${API}/registrations/${id}`, { withCredentials: true });
    setRegistrations(prev => prev.filter(r => r._id !== id));
  };

  const handleStatusChange = (id, status) => {
    setRegistrations(prev => prev.map(r => r._id === id ? { ...r, status } : r));
  };

  const togglePaid = async (id, currentPaid) => {
    const newPaid = !currentPaid;
    try {
      await axios.patch(`${API}/registrations/${id}/paid`, { paid: newPaid }, { withCredentials: true });
      setRegistrations(prev => prev.map(r => r._id === id ? { ...r, paid: newPaid } : r));
    } catch (e) { console.error(e); }
  };

  const normalize = (s = "") => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const filtered = registrations.filter(r => {
    if (search) {
      const q = normalize(search);
      const inTutor = normalize(r.nombre_tutor).includes(q);
      const inNinos = Array.isArray(r.ninos)
        ? r.ninos.some(n => normalize(n.nombre_nino).includes(q))
        : normalize(r.nombre_nino).includes(q);
      if (!inTutor && !inNinos) return false;
    }
    const matchStatus = filterStatus === "all" || r.status === filterStatus;
    const matchWeek = filterWeek === "all" || (filterWeek === "s1" && r.semana_1) || (filterWeek === "s2" && r.semana_2);
    return matchStatus && matchWeek;
  });

  const exportCSV = () => {
    const headers = ["Participante", "Talla", "Fecha nac.", "Alergias", "BTT", "Tutor", "Teléfono", "Email", "Semana 1", "Semana 2", "Madrugadores", "Recogida", "Precio (€)", "Estado", "Pagado", "Hermanos", "Fecha"];
    const rows = filtered.map(r => [
      `"${getParticipantes(r)}"`,
      r.talla_camiseta || "",
      r.fecha_nacimiento || "",
      `"${r.alergias || ""}"`,
      r.btt_bicicleta ? "Sí" : "No",
      `"${r.nombre_tutor || ""}"`,
      r.telefono_contacto || "",
      r.email_contacto || "",
      r.semana_1 ? "Sí" : "No",
      r.semana_2 ? "Sí" : "No",
      r.madrugadores ? "Sí" : "No",
      r.recogida_ampliada ? "Sí" : "No",
      r.precio_total || 0,
      STATUS_LABELS[r.status] || r.status,
      r.paid ? "Sí" : "No",
      r.num_hermanos > 1 ? r.num_hermanos : 1,
      r.created_at ? new Date(r.created_at).toLocaleDateString("es-ES") : "",
    ]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "inscripciones.csv"; a.click();
  };

  return (
    <div data-testid="registrations-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Inscripciones</h1>
          <p className="text-[#475569] font-['Montserrat'] text-sm">{registrations.length} inscripciones en total</p>
        </div>
        <button onClick={exportCSV} data-testid="export-csv-btn"
          className="flex items-center gap-2 border border-[#E2E8F0] bg-white text-[#475569] font-['Montserrat'] font-semibold px-4 py-2 rounded-lg hover:bg-[#F8FAFC] transition-all text-sm">
          <Download size={15} /> Exportar CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <Search size={15} color="#94A3B8" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre..."
            data-testid="search-input"
            className="pl-9 pr-4 py-2 border border-[#E2E8F0] rounded-lg font-['Montserrat'] text-sm focus:outline-none focus:border-[#0072A4] w-64"
          />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="filter-status"
          className="border border-[#E2E8F0] rounded-lg px-3 py-2 font-['Montserrat'] text-sm focus:outline-none focus:border-[#0072A4]">
          <option value="all">Todos los estados</option>
          <option value="pending">Pendientes</option>
          <option value="confirmed">Confirmadas</option>
          <option value="cancelled">Canceladas</option>
        </select>
        <select value={filterWeek} onChange={e => setFilterWeek(e.target.value)} data-testid="filter-week"
          className="border border-[#E2E8F0] rounded-lg px-3 py-2 font-['Montserrat'] text-sm focus:outline-none focus:border-[#0072A4]">
          <option value="all">Todas las semanas</option>
          <option value="s1">Semana 1</option>
          <option value="s2">Semana 2</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F8FAFC]">
                {["Participante", "Tutor", "Semana", "Servicios", "Estado", "Pagado", "Fecha", "Acciones"].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-['Montserrat'] text-xs font-semibold text-[#475569] uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-[#94A3B8] font-['Montserrat'] text-sm">No hay inscripciones que coincidan</td></tr>
              ) : filtered.map((reg) => {
                const StatusIcon = STATUS_ICONS[reg.status] || Clock;
                return (
                  <tr key={reg._id} className="hover:bg-[#F8FAFC] transition-colors" data-testid={`reg-row-${reg._id}`}>
                    <td className="px-4 py-3">
                      <div className="font-['Montserrat'] font-semibold text-[#0F172A] text-sm">
                        {getParticipantes(reg)}
                        {reg.num_hermanos > 1 && (
                          <span className="ml-1.5 text-[10px] font-semibold bg-[#3CB44B]/10 text-[#3CB44B] px-1.5 py-0.5 rounded-full align-middle">
                            Hermanos
                          </span>
                        )}
                      </div>
                      <div className="font-['Montserrat'] text-xs text-[#94A3B8]">{getTallas(reg)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-['Montserrat'] text-sm text-[#475569]">{reg.nombre_tutor}</div>
                      <div className="font-['Montserrat'] text-xs text-[#94A3B8]">{reg.telefono_contacto}</div>
                    </td>
                    <td className="px-4 py-3 font-['Montserrat'] text-xs text-[#475569]">
                      {reg.semana_1 && <span className="bg-[#0072A4]/10 text-[#0072A4] px-2 py-0.5 rounded-full mr-1">S1</span>}
                      {reg.semana_2 && <span className="bg-[#F58220]/10 text-[#F58220] px-2 py-0.5 rounded-full">S2</span>}
                    </td>
                    <td className="px-4 py-3 font-['Montserrat'] text-xs text-[#475569]">
                      {reg.madrugadores && <div>Madrugadores</div>}
                      {reg.recogida_ampliada && <div>Recogida ampl.</div>}
                      {!reg.madrugadores && !reg.recogida_ampliada && "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 font-['Montserrat'] text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLORS[reg.status] || "bg-gray-100 text-gray-700"}`}>
                        <StatusIcon size={12} />
                        {STATUS_LABELS[reg.status] || reg.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => togglePaid(reg._id, reg.paid)}
                        data-testid={`toggle-paid-${reg._id}`}
                        className={`px-3 py-1 rounded-full font-['Montserrat'] text-xs font-semibold transition-all ${reg.paid ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                      >
                        {reg.paid ? "Pagado" : "Pendiente"}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-['Montserrat'] text-xs text-[#94A3B8]">
                      {reg.created_at ? new Date(reg.created_at).toLocaleDateString("es-ES") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => setSelected(reg)} data-testid={`view-reg-${reg._id}`}
                          className="p-1.5 rounded-lg text-[#0072A4] hover:bg-[#F0F8FF] transition-colors" title="Ver detalles">
                          <Eye size={15} />
                        </button>
                        <button onClick={() => handleDelete(reg._id)} data-testid={`delete-reg-${reg._id}`}
                          className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Eliminar">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {selected && <RegistrationModal reg={selected} onClose={() => setSelected(null)} onStatusChange={handleStatusChange} />}
    </div>
  );
}
